/**
 * TraceabilityGraph Component
 * 
 * Interactive visualization of SSA traceability relationships.
 * Shows connections between Functions → Failure Conditions → FTA Events → FMEA Items.
 */

import React, { useState, useEffect } from 'react';
import { 
  GitBranch, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { safetyApi } from '@/services/safetyApi';
import type { TraceabilitySummary } from '@aerotage/shared';

interface TraceabilityGraphProps {
  assessmentId: string;
}

interface TraceabilityGaps {
  unlinkedEvents: string[];
  andGatesWithoutCMA: string[];
  fmeaItemsWithoutLinks: string[];
  failureConditionsWithoutFTA: string[];
}

export const TraceabilityGraph: React.FC<TraceabilityGraphProps> = ({ assessmentId }) => {
  const [summary, setSummary] = useState<TraceabilitySummary | null>(null);
  const [gaps, setGaps] = useState<TraceabilityGaps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'summary' | 'matrix' | 'graph'>('summary');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTraceabilityData();
  }, [assessmentId]);

  const loadTraceabilityData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [summaryData, gapsData] = await Promise.all([
        safetyApi.getTraceabilitySummary(assessmentId),
        safetyApi.getTraceabilityGaps(assessmentId),
      ]);
      setSummary(summaryData);
      setGaps(gapsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load traceability data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="w-12 h-12 text-warning mb-4" />
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={loadTraceabilityData} variant="secondary" className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'summary' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('summary')}
          >
            Summary
          </Button>
          <Button
            variant={viewMode === 'matrix' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('matrix')}
          >
            Matrix
          </Button>
          <Button
            variant={viewMode === 'graph' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('graph')}
          >
            Graph
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={loadTraceabilityData}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {viewMode === 'summary' && summary && gaps && (
          <SummaryView summary={summary} gaps={gaps} />
        )}
        {viewMode === 'matrix' && (
          <MatrixView assessmentId={assessmentId} />
        )}
        {viewMode === 'graph' && (
          <GraphView assessmentId={assessmentId} />
        )}
      </div>
    </div>
  );
};

// Summary View
interface SummaryViewProps {
  summary: TraceabilitySummary;
  gaps: TraceabilityGaps;
}

const SummaryView: React.FC<SummaryViewProps> = ({ summary, gaps }) => {
  const hasGaps = 
    gaps.unlinkedEvents.length > 0 ||
    gaps.andGatesWithoutCMA.length > 0 ||
    gaps.fmeaItemsWithoutLinks.length > 0 ||
    gaps.failureConditionsWithoutFTA.length > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Completeness Card */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          Traceability Completeness
        </h2>
        <div className="flex items-center gap-6">
          <div className="flex-shrink-0">
            <div 
              className={`w-24 h-24 rounded-full border-4 flex items-center justify-center ${
                summary.completenessScore >= 80 
                  ? 'border-success' 
                  : summary.completenessScore >= 50 
                  ? 'border-warning' 
                  : 'border-destructive'
              }`}
            >
              <span className="text-2xl font-bold text-foreground">
                {summary.completenessScore}%
              </span>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatBox label="Functions" value={summary.functionCount} />
            <StatBox label="Failure Conditions" value={summary.failureConditionCount} />
            <StatBox label="FTA Events" value={summary.ftaEventCount} />
            <StatBox label="FMEA Items" value={summary.fmeaItemCount} />
            <StatBox label="CMA Analyses" value={summary.cmaAnalysisCount} />
            <StatBox 
              label="Unlinked Events" 
              value={summary.unlinkedEventsCount} 
              warning={summary.unlinkedEventsCount > 0}
            />
          </div>
        </div>
      </div>

      {/* Gaps Card */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          {hasGaps ? (
            <AlertTriangle className="w-5 h-5 text-warning" />
          ) : (
            <CheckCircle className="w-5 h-5 text-success" />
          )}
          Traceability Gaps
        </h2>

        {hasGaps ? (
          <div className="space-y-4">
            {gaps.unlinkedEvents.length > 0 && (
              <GapSection
                title="Basic Events Without Reliability Data"
                items={gaps.unlinkedEvents}
                severity="warning"
              />
            )}
            {gaps.andGatesWithoutCMA.length > 0 && (
              <GapSection
                title="AND Gates Without CMA"
                items={gaps.andGatesWithoutCMA}
                severity="warning"
              />
            )}
            {gaps.fmeaItemsWithoutLinks.length > 0 && (
              <GapSection
                title="FMEA Items Without FTA Links"
                items={gaps.fmeaItemsWithoutLinks}
                severity="info"
              />
            )}
            {gaps.failureConditionsWithoutFTA.length > 0 && (
              <GapSection
                title="Failure Conditions Without FTA"
                items={gaps.failureConditionsWithoutFTA}
                severity="error"
              />
            )}
          </div>
        ) : (
          <p className="text-success">
            All traceability requirements are satisfied.
          </p>
        )}
      </div>
    </div>
  );
};

// Stat box component
const StatBox: React.FC<{ label: string; value: number; warning?: boolean }> = ({ 
  label, 
  value, 
  warning 
}) => (
  <div className="bg-background rounded-lg p-3 text-center">
    <div className={`text-xl font-bold ${warning ? 'text-warning' : 'text-foreground'}`}>
      {value}
    </div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>
);

// Gap section component
interface GapSectionProps {
  title: string;
  items: string[];
  severity: 'info' | 'warning' | 'error';
}

const GapSection: React.FC<GapSectionProps> = ({ title, items, severity }) => {
  const colors = {
    info: 'border-info bg-info/5',
    warning: 'border-warning bg-warning/5',
    error: 'border-destructive bg-destructive/5',
  };

  return (
    <div className={`border rounded-lg p-4 ${colors[severity]}`}>
      <h3 className="font-medium text-foreground mb-2">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.slice(0, 10).map((item) => (
          <span
            key={item}
            className="px-2 py-1 bg-background rounded text-sm text-muted-foreground"
          >
            {item}
          </span>
        ))}
        {items.length > 10 && (
          <span className="px-2 py-1 text-sm text-muted-foreground">
            +{items.length - 10} more
          </span>
        )}
      </div>
    </div>
  );
};

// Matrix View (placeholder)
const MatrixView: React.FC<{ assessmentId: string }> = ({ assessmentId: _assessmentId }) => {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <GitBranch className="w-16 h-16 text-muted-foreground mb-4" />
      <h2 className="text-lg font-medium text-foreground mb-2">
        Traceability Matrix
      </h2>
      <p className="text-muted-foreground mb-4">
        Interactive matrix view showing relationships between SSA elements.
      </p>
      <p className="text-sm text-muted-foreground">
        Coming soon - use the Summary view for now.
      </p>
    </div>
  );
};

// Graph View (placeholder)
const GraphView: React.FC<{ assessmentId: string }> = ({ assessmentId: _assessmentId }) => {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <GitBranch className="w-16 h-16 text-muted-foreground mb-4" />
      <h2 className="text-lg font-medium text-foreground mb-2">
        Interactive Graph
      </h2>
      <p className="text-muted-foreground mb-4">
        Explore SSA relationships with an interactive node graph.
      </p>
      <p className="text-sm text-muted-foreground">
        Coming soon - will use D3.js or similar for visualization.
      </p>
    </div>
  );
};
